import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API } from '../../../API';
import { Loader } from '../../../components/Loader/Loader';
import Modal from '../../../components/Modal';
import {
  AdminExecutionDetail,
  AdminExecutionListItem,
  AdminExecutionListResponse,
} from '../adminTypes';

import styles from './ExecutionsList.module.scss';

interface ExecutionsListProps {
  appId: string;
}

const statusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return styles.statusCompleted;
    case 'failed':
      return styles.statusFailed;
    case 'timeout':
      return styles.statusTimeout;
    case 'running':
      return styles.statusRunning;
    default:
      return styles.statusPending;
  }
};

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export const ExecutionsList: React.FC<ExecutionsListProps> = ({ appId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<AdminExecutionListResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminExecutionDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchExecutions = useCallback(() => {
    const params = new URLSearchParams();
    params.set('app_id', appId);
    params.set('page', String(page));
    params.set('size', '25');
    API.fetch(
      'GET',
      `/API/v2/internal/windmill/executions?${params.toString()}`,
    )
      .then((resp: AdminExecutionListResponse) => {
        setData(resp);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      });
  }, [appId, page]);

  useEffect(fetchExecutions, [fetchExecutions]);

  const openDetail = useCallback((exec: AdminExecutionListItem) => {
    setLoadingDetail(true);
    setDetailError(null);
    setSelected({
      ...exec,
      app_schema_version: 0,
      input: {},
      resolved_input: {},
      output: null,
      error_message: null,
      http_status_code: null,
      celery_task_id: null,
      completed_at: null,
    });
    API.fetch('GET', `/API/v2/internal/windmill/executions/${exec.id}`)
      .then((detail: AdminExecutionDetail) => {
        setSelected(detail);
        setDetailError(null);
      })
      .catch((err: Error) => {
        console.error('Failed to fetch execution details', err);
        setDetailError(err.message);
      })
      .finally(() => setLoadingDetail(false));
  }, []);

  if (!data && !loadError) return <Loader />;

  return (
    <div className={styles.root}>
      {loadError && <div className={styles.error}>{loadError}</div>}

      {data && data.objects.length === 0 ? (
        <div className={styles.empty}>
          {t('No executions yet for this app.')}
        </div>
      ) : (
        data && (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('When')}</th>
                  <th scope="col">{t('Status')}</th>
                  <th scope="col">{t('Mode')}</th>
                  <th scope="col">{t('Workspace')}</th>
                  <th scope="col">{t('User')}</th>
                  <th scope="col">{t('Duration')}</th>
                </tr>
              </thead>
              <tbody>
                {data.objects.map((exec) => (
                  <tr
                    key={exec.id}
                    className={styles.row}
                    onClick={() => openDetail(exec)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetail(exec);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={t('Open execution details for {{id}}', { id: exec.id }) as string}
                  >
                    <td className={styles.dim}>
                      {new Date(exec.created_at).toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${statusColor(exec.status)}`}
                      >
                        {t(`status.${exec.status}`)}
                      </span>
                    </td>
                    <td>
                      <span className={styles.modeBadge}>
                        {exec.execution_mode}
                      </span>
                    </td>
                    <td>{exec.workspace_name ?? `#${exec.workspace_id}`}</td>
                    <td>
                      {exec.triggered_by_user_name ??
                        `#${exec.triggered_by_user_id}`}
                    </td>
                    <td>{formatDuration(exec.duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data.total > data.size && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t('Previous')}
                </button>
                <span>
                  {t('Page {{page}} of {{total}}', {
                    page: data.page,
                    total: Math.ceil(data.total / data.size),
                  })}
                </span>
                <button
                  type="button"
                  disabled={data.page * data.size >= data.total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('Next')}
                </button>
              </div>
            )}
          </>
        )
      )}

      <Modal
        isVisible={Boolean(selected)}
        title={t('Execution details')}
        className={styles.detailModal}
        content={
          selected ? (
            <div className={styles.detail}>
              <div className={styles.detailRow}>
                <strong>ID:</strong> <code>{selected.id}</code>
              </div>
              <div className={styles.detailRow}>
                <strong>{t('Status')}:</strong>{' '}
                <span
                  className={`${styles.statusBadge} ${statusColor(selected.status)}`}
                >
                  {t(`status.${selected.status}`)}
                </span>
              </div>
              {loadingDetail ? (
                <div className={styles.loadingDetail}>{t('Loading...')}</div>
              ) : (
                <>
                  {detailError && (
                    <div className={styles.detailRow}>
                      <pre className={styles.errorBlock}>{detailError}</pre>
                    </div>
                  )}
                  {selected.error_message && (
                    <div className={styles.detailRow}>
                      <strong>{t('Error')}:</strong>
                      <pre className={styles.errorBlock}>
                        {selected.error_message}
                      </pre>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <strong>{t('Input')}:</strong>
                    <pre>{JSON.stringify(selected.input, null, 2)}</pre>
                  </div>
                  {selected.output !== null && selected.output !== undefined && (
                    <div className={styles.detailRow}>
                      <strong>{t('Output')}:</strong>
                      <pre>{JSON.stringify(selected.output, null, 2)}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null
        }
        footer={
          <button
            className="btn-reset"
            onClick={() => {
              setSelected(null);
              setDetailError(null);
            }}
          >
            {t('Close')}
          </button>
        }
        onClose={() => {
          setSelected(null);
          setDetailError(null);
        }}
      />
    </div>
  );
};
